/**
 * ComposePreviewView — 작성 중 최종 문서 미리보기(전체화면 read-only 뷰).
 *
 * 상세(ComposerReportDetail)와 **동일한 canonical 렌더러**(resolveForRender + PagedComposerView)를
 * 재사용해 "미리보기 == 상세" 파리티를 보장한다(신규 렌더러 금지).
 * - data = resolveForRender(template, values) — 현재 작성 입력값(content=values) 기준.
 *   liveData 미전달(작성 전) → live 바인딩은 undefined. approval 주입 없음(결재란은 빈 상태가 정상).
 * - 점검 집계·위험도 점수 등 서버 파생 항목은 저장 시 서버 compose가 확정한다(미리보기 단계엔 미반영).
 *
 * 재사용 가능하게 설계(외부 공개 작성 페이지에서도 후속 재사용 예정).
 *
 * 2026-07-26 web → rc 승격: admin 시뮬레이션이 web 배포판과 같은 미리보기를 렌더해야 하므로
 * rc 정본으로 이동. 앱 헤더 오프셋(useStickyChromeTop)은 host 레이아웃 지식이라 rc가 모른다 —
 * `stickyTopOffset` prop(host adapter)으로 반전(ComposeWritePageShell의 stickyTopOffset과 동형).
 */
import { useEffect, useMemo, useRef } from 'react';
import type { ReportTemplate } from '@safetyhub/shared';
import { resolveForRender } from './resolver';
import type { RenderData, SpecialComponentMap } from './types';
import { ApprovalBlockSpecial } from './approval';
import { PagedComposerView } from './PagedComposerView';

// 결재란(approvalBlock) special — 상세 ComposerReportDetail와 동일 구성(파리티).
const SPECIALS: SpecialComponentMap = {
  approvalBlock: ApprovalBlockSpecial,
};

export interface ComposePreviewViewProps {
  template: ReportTemplate;
  values: Record<string, unknown>;
  onBack: () => void;
  /**
   * 미리보기로 넘어오기 **직전**(뷰 전환 전) 작성 화면의 스크롤 위치.
   * 마운트 시점에 읽으면 늦다 — 그때는 이미 작성 화면이 사라져 문서가 짧아졌고, 브라우저가
   * 스크롤을 그 문서의 끝으로 잘라둔 뒤다(실측: 작성 끝 6139 → 마운트 시점엔 1894).
   * 그래서 전환을 일으키는 쪽이 클릭 시점에 잡아 넘겨준다. 생략 시 마운트 시점 값으로 폴백.
   */
  restoreScrollY?: number;
  /**
   * 상단 바 sticky top 오프셋(px) — host 앱 헤더 아래에 붙도록 host가 계산해 전달한다
   * (web=useStickyChromeTop: lg 미만 56 / lg+ 0). rc는 앱 레이아웃을 모르므로 prop으로 반전.
   * 생략 시 0(헤더 없는 host — admin 시뮬레이션 등).
   */
  stickyTopOffset?: number;
}

/**
 * 진행 중인 복원 루프의 취소 함수. **컴포넌트 지역이 아니라 모듈 스코프**여야 한다 —
 * 복원 루프는 언마운트된 인스턴스의 cleanup이 돌리는 것이라, 다음 인스턴스의 ref로는
 * 그것을 취소할 수 없다. 지역 ref로 두면 미리보기를 닫자마자(1초 안에) 다시 열 때
 * 이전 루프가 살아남아, 새 미리보기가 최상단으로 간 직후 옛 작성 위치로 되돌린다.
 * 의미 있는 복원 루프는 언제나 하나뿐이므로 단일 슬롯으로 충분하다.
 */
let cancelPendingRestore: (() => void) | null = null;

export function ComposePreviewView({
  template,
  values,
  onBack,
  restoreScrollY,
  stickyTopOffset,
}: ComposePreviewViewProps) {
  // 상세와 동일 렌더 데이터 조립. liveData 미전달 → live 바인딩 undefined(작성 전 정상).
  const data = useMemo<RenderData>(() => resolveForRender(template, values), [template, values]);
  // 앱 헤더(z-[60]) 아래에 붙도록 top 오프셋 + z-40대(모바일 앱 헤더 겹침·z-[60] 아래 문제 해소).
  const stickyTop = stickyTopOffset ?? 0;

  // 미리보기는 **항상 문서 처음부터** 보여준다.
  // 작성↔미리보기는 라우트 이동이 아니라 같은 페이지의 뷰 전환(setMode)이라, 그냥 두면 작성 화면에서
  // 내려둔 window 스크롤이 그대로 남아 문서 중간부터 보인다(ScrollRestoration은 라우트 변경에만 반응).
  // 돌아갈 때는 작성하던 위치를 되돌려 준다 — 긴 폼에서 위치를 잃지 않도록(Modal의 복원과 동형).
  const restoreScrollYRef = useRef<number | null>(null);
  useEffect(() => {
    // dev의 StrictMode는 effect를 mount→cleanup→mount로 두 번 돌린다. 그 중간 cleanup이 예약한
    // 복원이 두 번째 mount 뒤에 실행되면 진입 스크롤이 도로 내려간다(실측: 0으로 갔다가 1368 복귀).
    // 그래서 mount마다 먼저 대기 중인 복원을 취소한다.
    cancelPendingRestore?.();
    cancelPendingRestore = null;
    // 진입 당시 작성 위치는 **한 번만** 기록한다 — 두 번째 mount 시점엔 이미 0이라 덮으면 복원값을 잃는다.
    if (restoreScrollYRef.current === null) restoreScrollYRef.current = restoreScrollY ?? window.scrollY;
    // 이 뷰가 살아 있는 동안의 경로. 복원은 **떠나지 않았을 때만** 유효하다.
    // 작성↔미리보기는 같은 경로의 뷰 전환이라 이 값이 그대로고, 목록 등으로 이동하면 달라진다.
    // ⚠️ cleanup 시점에 읽으면 안 된다 — React Router는 location을 갱신한 뒤 언마운트를 커밋해서
    //    이미 새 경로가 잡히고, 그러면 가드가 영영 참이 되지 않아 새 화면 스크롤을 건드린다.
    const pathAtMount = window.location.pathname;
    window.scrollTo(0, 0);
    return () => {
      const target = restoreScrollYRef.current;
      // 진입 위치가 0이어도 복원 대상이다 — 건너뛰면 미리보기에서 내려둔 스크롤이 그대로 남아
      // 작성 화면이 중간부터 보인다(실측). effect가 아예 안 돈 경우(null)만 손대지 않는다.
      if (target === null) return;
      // 한 번만 부르면 안 된다 — 되돌아가는 시점의 작성 화면은 아직 최종 높이가 아니라서
      // 브라우저가 스크롤을 문서 끝으로 잘라버린다(실측: 목표 1368 → 842, 문서가 7157로 자라기 전).
      // 목표에 닿을 때까지 프레임마다 재시도하되 1초로 끊는다(ScrollRestoration의 재시도와 동형).
      const deadline = performance.now() + 1000;
      // 사용자가 직접 스크롤을 시작하면 즉시 손을 뗀다 — 안 그러면 최대 1초간 조작과 다툰다.
      // 'scroll'이 아니라 입력 이벤트를 듣는다(우리가 부르는 scrollTo도 scroll을 발생시키므로).
      let aborted = false;
      const abort = () => {
        aborted = true;
      };
      const USER_INPUT = ['wheel', 'touchstart', 'keydown'] as const;
      USER_INPUT.forEach((e) => window.addEventListener(e, abort, { passive: true, once: true }));
      const stop = () => {
        aborted = true;
        USER_INPUT.forEach((e) => window.removeEventListener(e, abort));
        // 끝난 루프를 모듈 슬롯에 남겨두지 않는다(더 새 루프가 이미 차지했으면 건드리지 않음).
        if (cancelPendingRestore === stop) cancelPendingRestore = null;
      };
      cancelPendingRestore = stop;
      // 목표에 한 번 닿았다고 멈추면 안 된다 — 그 뒤 레이아웃이 마저 정착하며 문서가 줄면
      // 브라우저 스크롤 앵커링이 위치를 끌어올린다(실측: 1368 도달 후 문서가 7682→7157로 줄자 842로 밀림).
      // 그래서 "목표에 있고 문서 높이가 그대로"인 프레임이 연속으로 나올 때까지 지켜본다.
      const STABLE_FRAMES = 3;
      let stable = 0;
      let lastHeight = -1;
      const tryRestore = () => {
        // 페이지를 떠났으면 중단 — 새 화면의 스크롤을 건드리면 안 된다.
        if (aborted || window.location.pathname !== pathAtMount) return stop();
        const height = document.documentElement.scrollHeight;
        const atTarget = Math.abs(window.scrollY - target) < 2;
        if (!atTarget) window.scrollTo(0, target);
        stable = atTarget && height === lastHeight ? stable + 1 : 0;
        lastHeight = height;
        if (stable >= STABLE_FRAMES || performance.now() > deadline) return stop();
        requestAnimationFrame(tryRestore);
      };
      requestAnimationFrame(tryRestore);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 상단 얇은 바 — 미리보기 제목 + 작성으로 돌아가기 */}
      <div
        className="sticky z-40 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-2.5"
        style={{ top: stickyTop }}
      >
        <h1 className="text-sm font-semibold text-gray-900">미리보기</h1>
        <button
          type="button"
          onClick={onBack}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-control hover:bg-gray-100 transition-colors"
        >
          작성으로 돌아가기
        </button>
      </div>

      <div className="max-w-4xl xl:max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* 안내 배너 — 서버 파생 항목 안내 */}
        <p className="text-xs text-brand-700 bg-brand-50 rounded-card px-3 py-2">
          입력값 기준 미리보기입니다. 점검 집계·위험도 점수 등 서버 파생 항목은 저장 시 확정됩니다.
        </p>

        {/* 양식 본문 — 상세와 동일 래핑(overflow-x-auto + PagedComposerView). */}
        <div className="overflow-x-auto">
          <PagedComposerView template={template} data={data} specialComponents={SPECIALS} />
        </div>
      </div>
    </div>
  );
}
